import Icon from '@mui/material/Icon';
import type { ReactElement } from 'react';
import { NavLink } from 'react-router-dom';

import { cn } from '@/lib/utils';

import type { SidebarItemClickHandler, SidebarNavItem } from '../types';

import SidebarItemTag from './SidebarItemTag';

const ITEM_CLASSES =
  'vtwo:group vtwo:flex vtwo:h-8 vtwo:w-full vtwo:items-center vtwo:gap-2 vtwo:rounded-lg vtwo:px-2 vtwo:text-sm vtwo:font-medium vtwo:leading-[18px] vtwo:no-underline! vtwo:transition-colors vtwo:hover:bg-[#2e3032]/60';
const IDLE_CLASSES = 'vtwo:text-white/80!';
const ACTIVE_CLASSES = 'vtwo:bg-[#2e3032] vtwo:text-white! vtwo:hover:bg-[#2e3032]';
// Most icons are drawn dark for a light background; on the dark sidebar they are shown white,
// brighter on the current page (NavLink marks it with aria-current). `colourIcon` ones keep their colours.
const ICON_CLASSES =
  'vtwo:brightness-0 vtwo:invert vtwo:opacity-80 vtwo:group-aria-[current=page]:opacity-100';

interface SidebarItemProps {
  item: SidebarNavItem;
  onItemClick: SidebarItemClickHandler;
}

export default function SidebarItem({ item, onItemClick }: SidebarItemProps): ReactElement | null {
  if (item.display === false) return null;

  const content = (
    <>
      <SidebarItemIcon item={item} />
      <span className="vtwo:min-w-0 vtwo:flex-1 vtwo:truncate">{item.title}</span>
      <SidebarItemTag item={item} />
    </>
  );

  if (item.type === 'link') {
    return (
      <a
        href={item.url}
        target={item.target ?? '_blank'}
        className={cn(ITEM_CLASSES, IDLE_CLASSES)}
        onClick={(event) => onItemClick(item, event)}
      >
        {content}
      </a>
    );
  }

  return (
    <NavLink
      to={item.url ?? ''}
      end={item.end}
      className={({ isActive }) => cn(ITEM_CLASSES, isActive ? ACTIVE_CLASSES : IDLE_CLASSES)}
      onClick={() => onItemClick(item)}
    >
      {content}
    </NavLink>
  );
}

function SidebarItemIcon({ item }: { item: SidebarNavItem }): ReactElement | null {
  if (item.img) {
    return (
      <img
        className={cn(
          'vtwo:size-3.5 vtwo:shrink-0 vtwo:object-contain',
          !item.colourIcon && ICON_CLASSES
        )}
        src={item.img}
        alt=""
        aria-hidden
      />
    );
  }

  if (!item.icon) return null;

  return (
    <Icon
      className="vtwo:shrink-0 vtwo:text-[16px]! vtwo:text-white/55 vtwo:group-aria-[current=page]:text-white/70"
      aria-hidden
    >
      {item.icon}
    </Icon>
  );
}
